import { gql, useLazyQuery, useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import { Select } from "components/base/Form";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import DateRangePicker from "pages/analytics/DateRangePicker";
import { useDateRangeFrom } from "hooks/useDateRange";
import { formatDate } from "react-day-picker/moment";
import { useEffect, useState } from "react";
import ReportChart from "./ReportChart";
import ReportDetails from "./ReportDetails";

const SHIPMENT_ANALYTICS_QUARTER_REPORT = gql`
  query SHIPMENT_ANALYTICS_QUARTER_REPORT(
    $group: String!
    $startDate: String!
    $endDate: String!
  ) {
    shipmentGoals(group: $group, startDate: $startDate, endDate: $endDate)
  }
`;

const FETCH_SHIPMENT_GOAL_DETAILS = gql`
  query FETCH_SHIPMENT_GOAL_DETAILS(
    $goal: String!
    $period: String!
    $group: String!
    $startDate: String!
    $endDate: String!
  ) {
    shipmentGoalDetails(
      goal: $goal
      period: $period
      group: $group
      startDate: $startDate
      endDate: $endDate
    )
  }
`;

function formatRateValue(value) {
  const rate = Number(value);
  if (!Number.isFinite(rate)) return value;
  const roundedRate = Number.isInteger(rate)
    ? String(rate)
    : rate.toFixed(2).replace(/\.?0+$/, "");
  return `${roundedRate}%`;
}

function calculateAverageProductionDays(shipments) {
  const trackedShipments = shipments.filter((item) => item.track_leadtime);
  if (!trackedShipments.length) return 0;

  const total = trackedShipments.reduce(
    (sum, item) => sum + item.production_days,
    0,
  );
  return Number((total / trackedShipments.length).toFixed(1));
}

const ReportDetailNumber = ({ children }) => (
  <span className="font-semibold text-gray-900 dark:text-gray-100">
    {children}
  </span>
);

const ReportDetailsContent = ({ label, value, unit, right, children }) => (
  <div className="flex flex-col gap-6">
    <div className="flex items-baseline justify-between gap-8 text-lg text-gray-500 dark:text-gray-400">
      <div>
        <span>{label}</span>
        <span className="ml-3">
          <ReportDetailNumber>{value}</ReportDetailNumber>
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
    {children}
  </div>
);

const ShipmentGoals = () => {
  const [group, setGroup] = useState("Q");
  const [{ start, end }, { setStart, setEnd }] = useDateRangeFrom(
    new Date(new Date().getFullYear() - 2, 0, 1),
  );
  const [productionDaysOverride, setProductionDaysOverride] = useState(null);
  const variables = {
    group,
    startDate: formatDate(start, "YYYY-MM-DD"),
    endDate: formatDate(end, "YYYY-MM-DD"),
  };
  const { loading, error, data } = useQuery(SHIPMENT_ANALYTICS_QUARTER_REPORT, {
    variables,
  });
  const [fetchGoalDetails] = useLazyQuery(FETCH_SHIPMENT_GOAL_DETAILS, {
    fetchPolicy: "network-only",
  });
  const modal = useModals();

  useEffect(() => {
    setProductionDaysOverride(null);
  }, [group, start, end]);

  if (loading) return <Spinner />;
  if (error) return <Errors />;

  const shipmentGoals = JSON.parse(data.shipmentGoals);
  const shipmentGoalsDelayDays = shipmentGoals.delay_days || [];
  const shipmentGoalsProductionDays =
    productionDaysOverride || shipmentGoals.production_days || [];
  const shipmentGoalsOnTimeDelivery = shipmentGoals.on_time_delivery || [];
  const shipmentGoalsContainerLoad = shipmentGoals.container_load || [];
  const shipmentGoalsShipments = shipmentGoals.shipments || [];

  async function getGoalDetails(goal, period) {
    try {
      const { data } = await fetchGoalDetails({
        variables: { goal, period, ...variables },
      });
      return JSON.parse(data.shipmentGoalDetails);
    } catch (error) {
      Alert("error", error.message);
      return null;
    }
  }

  async function onClickAveDelayDaysBarHandler(e) {
    const modalProps = {
      title: e.name,
    };
    modal.present({ ...modalProps, children: <Spinner /> });
    const details = await getGoalDetails("delay_days", e.name);
    if (!details) return modal.hide();
    modal.present({
      ...modalProps,
      children: (
        <ReportDetailsContent label="Average Delay Days" value={e.value}>
          <ReportDetails shipments={details.shipments} type="AVE_DELAY_DAYS" />
        </ReportDetailsContent>
      ),
    });
  }

  async function onClickOnTimeDeliveryBarHandler(e) {
    const modalProps = {
      title: e.name,
    };
    modal.present({ ...modalProps, children: <Spinner /> });
    const details = await getGoalDetails("on_time_delivery", e.name);
    if (!details) return modal.hide();
    const shipmentCount = details.shipments.filter(
      (shipment) => shipment.is_included,
    ).length;
    const excludedShipmentCount = details.shipments.length - shipmentCount;
    modal.present({
      ...modalProps,
      children: (
        <ReportDetailsContent
          label="On Time Delivery"
          value={formatRateValue(e.value)}
          right={
            <>
              {details.shipments.length} - {excludedShipmentCount} ={" "}
              <ReportDetailNumber>{shipmentCount}</ReportDetailNumber> Shipments
            </>
          }
        >
          <ReportDetails
            shipments={details.shipments}
            type="ON_TIME_DELIVERY"
          />
        </ReportDetailsContent>
      ),
    });
  }

  async function onClickFullContainerLoadBarHandler(e) {
    const modalProps = {
      title: e.name,
    };
    modal.present({ ...modalProps, children: <Spinner /> });
    const details = await getGoalDetails("container_load", e.name);
    if (!details) return modal.hide();
    modal.present({
      ...modalProps,
      children: (
        <ReportDetailsContent
          label="Full Container Load"
          value={formatRateValue(e.value)}
        >
          <ReportDetails
            shipments={details.shipments}
            type="FULL_CONTAINER_LOAD"
          />
        </ReportDetailsContent>
      ),
    });
  }

  function showProductionDaysModal(e, shipments) {
    modal.present({
      title: e.name,
      children: (
        <ReportDetailsContent label="Average Production Days" value={e.value}>
          <ReportDetails
            shipments={shipments}
            type="AVE_PRODUCTION_DAYS"
            onTrackLeadtimeChange={async (shipment) => {
              const refreshedShipments = shipments.map((item) =>
                item.id === shipment.id
                  ? { ...item, track_leadtime: !item.track_leadtime }
                  : item,
              );
              const averageProductionDays =
                calculateAverageProductionDays(refreshedShipments);
              setProductionDaysOverride((current) =>
                (current || shipmentGoals.production_days).map((item) =>
                  item.group === e.name
                    ? { ...item, ave_production_days: averageProductionDays }
                    : item,
                ),
              );

              showProductionDaysModal(
                { ...e, value: averageProductionDays },
                refreshedShipments,
              );
            }}
          />
        </ReportDetailsContent>
      ),
    });
  }

  async function presentProductionDaysModal(e) {
    modal.present({
      title: e.name,
      children: <Spinner />,
    });
    const details = await getGoalDetails("production_days", e.name);
    if (!details) return modal.hide();
    showProductionDaysModal(e, details.shipments);
  }

  async function onClickDeliveredToPortBarHandler(e) {
    const modalProps = {
      title: e.name,
    };
    modal.present({ ...modalProps, children: <Spinner /> });
    const details = await getGoalDetails("shipments", e.name);
    if (!details) return modal.hide();
    modal.present({
      ...modalProps,
      children: (
        <ReportDetailsContent
          label="Delivered to Port"
          value={e.value}
          unit="Shipments"
        >
          <ReportDetails
            shipments={details.shipments}
            type="DELIVERED_TO_PORT"
          />
        </ReportDetailsContent>
      ),
    });
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-4">
        <DateRangePicker
          start={start}
          end={end}
          setStart={setStart}
          setEnd={setEnd}
        />

        <Select value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="M">by Month</option>
          <option value="Q">by Quarter</option>
          <option value="Y">by Year</option>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
        <ReportChart
          title="Average Delay Days"
          subtitle="Shipments are grouped base on their `delivered to port` date."
          data={shipmentGoalsDelayDays.map((i) => ({
            ...i,
            name: i.group,
            value: i.ave_delay_days,
          }))}
          fillColor="#f687b3"
          valueName="Days"
          onClick={onClickAveDelayDaysBarHandler}
        />

        <ReportChart
          title="Average Production Days"
          subtitle="Shipments are grouped base on their `delivered to port` date."
          data={shipmentGoalsProductionDays.map((i) => ({
            ...i,
            name: i.group,
            value: i.ave_production_days,
          }))}
          fillColor="#68D391"
          valueName="Days"
          onClick={presentProductionDaysModal}
        />

        <ReportChart
          title="On Time Delivery Rate"
          subtitle="Shipments are grouped base on their `delivered to port` date."
          data={shipmentGoalsOnTimeDelivery.map((i) => ({
            ...i,
            name: i.group,
            value: i.rate,
          }))}
          fillColor="#3b82f6"
          valueName="Rate %"
          onClick={onClickOnTimeDeliveryBarHandler}
        />

        <ReportChart
          title="Full Container Load"
          subtitle="Shipments are grouped base on their `delivered to port` date."
          data={shipmentGoalsContainerLoad.map((i) => ({
            ...i,
            name: i.group,
            value: i.avg_full_container_load,
          }))}
          fillColor="#84cc16"
          valueName="Rate %"
          onClick={onClickFullContainerLoadBarHandler}
        />

        <ReportChart
          width="w-full"
          title="Delivered to Port"
          subtitle="Shipments are grouped base on their `delivered to port` date."
          data={shipmentGoalsShipments.map((i) => ({
            ...i,
            name: i.group,
            value: i.total_shipments,
          }))}
          fillColor="#9333ea"
          valueName="Shipments"
          onClick={onClickDeliveredToPortBarHandler}
        />
      </div>
    </section>
  );
};

export default ShipmentGoals;
